/**
 * Meta Description Transformer
 * Transforms WooRank description criteria data into React component-ready format
 */

import {
  getStatus,
  getIconType,
  getImportance,
  getSolvability,
  DESCRIPTION_THRESHOLDS,
} from '../../constants/seoMappings';

/**
 * Generates a scenario-based message for the meta description
 * Validates by both character count and pixel width
 * @param {Object} data - The description criteria data
 * @param {string} url - The URL being analyzed
 * @returns {string} - The appropriate message based on the scenario
 */
const generateMessage = (data, url = '') => {
  // Missing description scenario
  if (!data?.value) {
    return `The meta description for ${url || 'this page'} is missing.`;
  }

  const length = data?.length || 0;
  const pixelLength = data?.pixelLength || 0;

  // Check character length issues first
  if (length > DESCRIPTION_THRESHOLDS.MAX_LENGTH) {
    return `Meta description is too long (${length} characters) – limit to ${DESCRIPTION_THRESHOLDS.MAX_LENGTH} characters.`;
  }

  if (length < DESCRIPTION_THRESHOLDS.MIN_LENGTH) {
    return `Meta description is too short (${length} characters) – aim for ${DESCRIPTION_THRESHOLDS.MIN_LENGTH}-${DESCRIPTION_THRESHOLDS.MAX_LENGTH} characters.`;
  }

  // Check pixel width issues
  if (pixelLength > DESCRIPTION_THRESHOLDS.MAX_PIXELS) {
    return `Meta description pixel width is too wide (${pixelLength}px) – limit to ${DESCRIPTION_THRESHOLDS.MAX_PIXELS}px.`;
  }

  if (pixelLength < DESCRIPTION_THRESHOLDS.MIN_PIXELS) {
    return `Meta description pixel width is too narrow (${pixelLength}px) – aim for ${DESCRIPTION_THRESHOLDS.MIN_PIXELS}-${DESCRIPTION_THRESHOLDS.MAX_PIXELS}px.`;
  }

  // Good length scenario
  return `Meta description length is good (${length} characters, ${pixelLength}px).`;
};

/**
 * Transforms the description criteria from WooRank API response
 * @param {Object} descriptionCriteria - The description criteria object from the API
 * @param {string} url - The URL being analyzed
 * @returns {Object} - Transformed meta description data for React components
 */
export const transformMetaDescription = (descriptionCriteria, url = '') => {
  // Handle missing criteria entirely
  if (!descriptionCriteria) {
    return {
      status: 'error',
      iconType: 'error',
      importance: { level: 0, text: '' },
      solvability: { level: 0, text: '' },
      isEmpty: true,
      message: `The meta description for ${url || 'this page'} is missing.`,
      data: null,
    };
  }

  const data = descriptionCriteria?.data || null;
  const wooRankStatus = descriptionCriteria?.status || 'bad';
  const importance = descriptionCriteria?.importance || 0;
  const solvability = descriptionCriteria?.solvability || 0;

  // Check if data is empty/missing
  const isEmpty = !data?.value;

  return {
    status: getStatus(wooRankStatus),
    iconType: getIconType(wooRankStatus),
    importance: getImportance(importance),
    solvability: getSolvability(solvability),
    isEmpty,
    message: generateMessage(data, url),
    data: isEmpty ? null : {
      value: data?.value || '',
      length: data?.length || 0,
      pixelLength: data?.pixelLength || 0,
    },
  };
};

export default transformMetaDescription;

